import { useLocation, useSearchParams } from 'react-router'
import { useCart } from '@/hooks/use-cart'
import { useAppSelector } from '@/redux/hooks'
import {
  ApiReturnedItemSubproduct,
  ItemActions
} from '@/redux/return/return-slice'
import {
  allocatePromotionalUnits,
  keepsPromotion,
  promotionalLineTotal
} from '@/app/full-catalog/helpers/promotional-exchange'
import type { NewItem } from '@/types/new-item'

const cartItemKey = (item: NewItem) =>
  `${item.returnedItemId}:${item.productVariant.variantId}`

export function useCredit() {
  const [searchParams] = useSearchParams()

  const { state } = useLocation()

  const returnedItemState = state?.returnedItem as ApiReturnedItemSubproduct

  const returnedItems = useAppSelector((store) => store.return.returnedItems)
  const orderProducts = useAppSelector((store) => store.order.orderProducts)

  const { cartItems } = useCart()

  const currentReturnedItemId = searchParams.get('lineItemId')

  const currentReturnedItem =
    returnedItems.find((item) => item.lineItemId === currentReturnedItemId) ??
    returnedItemState

  const currentReturnedItemAction = currentReturnedItem?.action

  const returnedItemInfo = orderProducts.find(
    (item) => item.lineItemId === currentReturnedItemId
  )

  const returnedItemPrice =
    (((returnedItemInfo?.product.total ?? 0) /
      (returnedItemInfo?.product.quantity ?? 1)) *
      (currentReturnedItem?.item.quantity ?? 1)) /
    100

  const returnedItemsFullCatalog = returnedItems.filter(
    (item) => item.action === ItemActions.ToExchangeFullCatalog
  )

  const totalAvailableCredit =
    returnedItemsFullCatalog
      .map((item) => {
        const product = orderProducts.find(
          (product) => product.lineItemId === item.lineItemId
        )
        if (product) {
          return (
            ((product.product.total ?? 0) / (product.product.quantity ?? 1)) *
            item.item.quantity
          )
        }
        return 0
      })
      .reduce((acc, price) => acc + price / 100, 0) +
    (currentReturnedItemAction === ItemActions.ToReturn ? returnedItemPrice : 0)

  const returnedItemFor = (returnedItemId: string) =>
    returnedItems.find((item) => item.lineItemId === returnedItemId) ??
    (returnedItemState?.lineItemId === returnedItemId
      ? returnedItemState
      : undefined)

  // The summary covers the returned quantity, never more than the promotion carried
  const promotionalUnitsOf = (returnedItem?: ApiReturnedItemSubproduct) => {
    const carried = returnedItem?.promotionalExchange?.carried_quantity ?? 0

    return Math.min(carried, returnedItem?.item.quantity ?? 0)
  }

  const promotionalCartLines = cartItems
    .filter((item) => returnedItemFor(item.returnedItemId)?.promotionalExchange)
    .map((item) => {
      const returnedItem = returnedItemFor(item.returnedItemId)

      return {
        key: cartItemKey(item),
        returnedItemId: item.returnedItemId,
        quantity: item.quantity,
        keepsPromotion: keepsPromotion(returnedItem?.promotionalExchange, {
          productId: item.productVariant.productId,
          collectionIds: item.collectionIds
        }),
        promotionalUnits: promotionalUnitsOf(returnedItem)
      }
    })

  const allocation = allocatePromotionalUnits(promotionalCartLines)

  const promotionalUnitsForCartItem = (item: NewItem) =>
    allocation.unitsByLine.get(cartItemKey(item)) ?? 0

  const cartItemTotalPrice = (item: NewItem) => {
    const promotionalExchange = returnedItemFor(
      item.returnedItemId
    )?.promotionalExchange
    const promotionalUnits = promotionalUnitsForCartItem(item)

    if (!promotionalExchange || promotionalUnits === 0) {
      return item.quantity * item.price
    }

    return promotionalLineTotal(
      promotionalExchange,
      item.price,
      item.quantity,
      promotionalUnits
    )
  }

  const newItemsTotalPrice = cartItems.reduce(
    (acc, item) => acc + cartItemTotalPrice(item),
    0
  )

  const computeAmountToPay = () => {
    if (newItemsTotalPrice > 0) {
      const payAmount = newItemsTotalPrice - totalAvailableCredit
      if (payAmount > 0) {
        return payAmount
      } else {
        return 0
      }
    } else {
      return 0
    }
  }

  const amountToPay = computeAmountToPay()
  const spentCredit =
    newItemsTotalPrice >= totalAvailableCredit
      ? totalAvailableCredit
      : newItemsTotalPrice
  const remainingCredit = totalAvailableCredit - spentCredit

  const promotionalExchange = currentReturnedItem?.promotionalExchange
  const availablePromotionalUnits = promotionalExchange
    ? (allocation.unusedUnits.get(currentReturnedItem.lineItemId) ??
      promotionalUnitsOf(currentReturnedItem))
    : 0

  return {
    totalAvailableCredit,
    spentCredit,
    amountToPay,
    remainingCredit,
    // Promotion of the item being exchanged, and how many units of it are still free
    promotionalExchange,
    availablePromotionalUnits,
    promotionalUnitsForCartItem,
    cartItemTotalPrice
  }
}
